import {TransferType} from '@transactions/graphql';
import Money from 'common/Money';
import * as React from 'react';
import {AmountDisplay} from 'src/AmountDisplay';
import {ConsumptionsList} from 'src/ConsumptionsList';
import {BasisSection} from 'src/DetailComponents/BasisSection';
import Section from 'src/DetailComponents/Section';
import {DetailActions} from 'src/DetailComponents/Transaction/DetailActions';
import {TransferDisplay} from 'src/DetailComponents/Transaction/Display';
import {useMoneyFormatter} from 'src/hooks/useUnits';
import {totalsByCurrency} from 'src/lib/currencyTotals';
import {TRANSFER_TYPE_LABELS} from 'src/TransferKindSelect';
import {Container, DetailHeader} from '.';
import type {DetailTransactionFragment} from '@transactions/graphql';

type Props = {
  transfer: DetailTransactionFragment;
  onReset?: () => void;
  /** A line to pick out: the one a tap on an activity row came from. */
  highlightLineId?: string | null;
};

const formatTransferTitle = (transferType: TransferType | null): string => {
  if (transferType && transferType in TRANSFER_TYPE_LABELS) {
    return TRANSFER_TYPE_LABELS[transferType];
  }
  return 'Transfer';
};

const TransferDetail = (props: Props) => {
  const {transfer} = props;
  const formatMoney = useMoneyFormatter();

  const isConversion = transfer.transferType === TransferType.Conversion;

  // A transfer nets to zero; the amount moved is the sum of one side.
  const movedAmount = transfer.consumptions
    .filter((c) => c.amount > 0)
    .reduce((sum, c) => sum + c.amount, 0);

  // A conversion's legs live in two currencies; render them as
  // "-$137.49 → +¥20,000" (outgoing first) from the consumed raws.
  //
  // `rawTransaction` can be briefly absent: mutations on this page return the
  // UnifiedTransaction fragment, whose consumptions carry only
  // {rawTransactionId, amount}, so Apollo rewrites the cached consumption
  // objects without the nested raw until the detail query refetches. Drop
  // those legs rather than dereferencing undefined — a partial header for one
  // render beats a crash.
  const conversionLegs = isConversion
    ? totalsByCurrency(
        transfer.consumptions
          .filter((c) => c.rawTransaction?.currency != null)
          .map((c) => ({
            amount: c.amount,
            currency: c.rawTransaction.currency,
          })),
      ).sort((a, b) => a.amount - b.amount)
    : [];

  return (
    <Container>
      <DetailHeader
        // The payee (the counterparty) names the page when present — "Amazon"
        // beats "Refund" — with the kind still shown in the Details rows.
        title={
          transfer.payee || formatTransferTitle(transfer.transferType ?? null)
        }
        // Muted: money that moved between accounts, not money spent.
        amount={
          isConversion ? (
            <span className="text-muted-foreground">
              {conversionLegs.map((leg) => formatMoney(leg)).join(' → ')}
            </span>
          ) : movedAmount > 0 ? (
            <div className="text-muted-foreground">
              <AmountDisplay
                align="left"
                amount={Money.fromInt(movedAmount, transfer.currency)}
                variant="plain"
              />
            </div>
          ) : null
        }
        actions={
          <DetailActions transaction={transfer} onReset={props.onReset} />
        }
      />
      <Section header="Details">
        <TransferDisplay transfer={transfer} />
      </Section>
      <BasisSection transaction={transfer} />
      <Section header="Lines">
        <ConsumptionsList
          consumptions={transfer.consumptions}
          highlightId={props.highlightLineId}
          currency={transfer.currency}
        />
      </Section>
    </Container>
  );
};

export {TransferDetail};
